import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import css from './SearchableDropdown.module.scss';

const SearchableDropdown = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Search and select an option',
  required = false,
  disabled = false,
  error = '',
  name,
  addSearchTerm = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const option = options.find(opt => opt.value === value);
    setSelectedOption(option);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setSearchTerm('');
    setIsOpen(false);
    if (addSearchTerm) {
      addSearchTerm('')
    }
    onChange({
      target: {
        name,
        value: option.value
      }
    });
  };

  const handleAddSearchTerm = (searchTerm) => {
    setIsOpen(false)
    addSearchTerm(searchTerm)
  }

  return (
    <div className={css.dropdownContainer} ref={dropdownRef}>
      {label && (
        <label className={css.label}>
          {label}
          {required && <span className={css.required}>*</span>}
        </label>
      )}
      <div className={`${css.dropdown} ${error ? css.error : ''} ${disabled ? css.disabled : ''}`}>
        <input
          type="text"
          className={css.input}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedOption ? selectedOption.label : placeholder}
          disabled={disabled}
          required={required}
          name={name}
        />
        {isOpen && !disabled && (
          <div className={css.optionsContainer}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div key={option.value}
                     className={`${css.option} ${selectedOption?.value === option.value ? css.selected : ''} ${option.disabled ? css.disabledOption : ''}`}
                     onClick={() => !option.disabled && handleOptionClick(option)}
                >
                  {option.label}
                </div>
              ))
            ) : searchTerm.trim() && addSearchTerm ? (
              <div
                className={css.option}
                onClick={() => handleAddSearchTerm(searchTerm)}
              >
                Select "{searchTerm}"
              </div>
            ) : (
              <div className={css.noOptions}>No options found</div>
            )}
          </div>
        )}
      </div>
      {error && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

SearchableDropdown.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
};

export default SearchableDropdown;
